import { useToast } from '@/components/ui/toast';
import { useAuth } from '@/contexts/auth-context';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDisplayName, getPhotoAlt, getUserInitials } from './user-menu.domain';

export function useUserMenu() {
  const { user, signOut, logoutAndForget } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      setAccountDialogOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: 'Error',
        description: 'Failed to sign out',
        variant: 'destructive',
      });
    }
  };

  const handleLogoutAndForget = async () => {
    setIsLoggingOut(true);
    try {
      const result = await logoutAndForget();
      setConfirmDialogOpen(false);
      setAccountDialogOpen(false);
      window.history.replaceState({}, '', '/auth');

      if (result.revoked) {
        toast({
          title: 'Logged out',
          description: 'Signed out and credentials removed.',
          variant: 'default',
        });
      } else {
        toast({
          title: 'Logged out',
          description:
            'Signed out and credentials removed. Google revocation could not be confirmed.',
          variant: 'default',
        });
      }
      navigate('/auth');
    } catch (error: unknown) {
      console.error('Error logging out and forgetting:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to disconnect and sign out',
        variant: 'destructive',
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const openConfirmDialog = () => {
    setAccountDialogOpen(false);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => setConfirmDialogOpen(false);

  if (!user) {
    return {
      user: null,
      shouldShow: false,
      accountDialogOpen: false,
      setAccountDialogOpen: () => {},
      confirmDialogOpen: false,
      setConfirmDialogOpen: () => {},
      isLoggingOut: false,
      displayName: '',
      initials: 'U',
      photoAlt: 'User',
      photoURL: undefined,
      email: undefined,
      handleSignOut: async () => {},
      handleLogoutAndForget: async () => {},
      openConfirmDialog: () => {},
      closeConfirmDialog: () => {},
    };
  }

  return {
    user,
    shouldShow: true,
    accountDialogOpen,
    setAccountDialogOpen,
    confirmDialogOpen,
    setConfirmDialogOpen,
    isLoggingOut,
    displayName: getDisplayName(user),
    initials: getUserInitials(user),
    photoAlt: getPhotoAlt(user),
    photoURL: user.photoURL || undefined,
    email: user.email ?? undefined,
    handleSignOut,
    handleLogoutAndForget,
    openConfirmDialog,
    closeConfirmDialog,
  };
}
